import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {ReactNode} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {RolledUpTimelineItem} from '../../utils/timeline-rollups'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import type {
  IssueTimelineIssueFragment$data,
  IssueTimelineIssueFragment$key,
} from './__generated__/IssueTimelineIssueFragment.graphql'
import type {TimelineIssue} from './fetch-timeline-items'
import type {EventProps, IssueTimelineItemFragment} from './IssueTimelineItem'
import type {LoadMoreCallbackFn} from './LoadMore'
import {TimelinePanel} from './TimelinePanel'
import {useTimelineItems} from './use-timeline-items'
import type {UseTimelineItemsCoreResult} from './use-timeline-items-core'
import {useTimelineItemsTanstack} from './use-timeline-items-tanstack'

type LoadItem = {
  type: 'load'
  position: 'top' | 'bottom'
  loadFromTop: LoadMoreCallbackFn
  loadFromBottom: LoadMoreCallbackFn
  numberOfRemainingItems: number
  isCurrentlyLoading?: boolean
}

type EventItem = {type: 'event'} & RolledUpTimelineItem<IssueTimelineItemFragment>
export type TimelineItem = EventItem | LoadItem

/**
 * Temporary type for parsing the timeline items and grouping them into section elements
 * for improve the accessibility of the event items by adding landmarks for navigation.
 */
export type RenderItem = {
  /**
   * Controls whether the item has already been added to the grouped events or not.
   *
   * Only used in events and not in load items, graceful degratation items or issue comments.
   */
  isAddedToGroupedEvents: boolean
  timelineItem: TimelineItem
  render: ReactNode
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const mainTimelineFragment = graphql`
  fragment IssueTimelineIssueFragment on Issue
  @argumentDefinitions(skip: {type: "Int"}, count: {type: "Int", defaultValue: 15}) {
    id
    url
    repository {
      id
      nameWithOwner
      visibility
      viewerCanPush
    }
    ...useTimelineItemsFrontFragment @arguments(count: $count, skip: $skip)
    ...useTimelineItemsBackFragment
  }
`

export type IssueTimelineSecondaryFragment = RelayCompat<Pick<IssueSecondaryNode, 'isTransferInProgress'>>

type IssueTimelineProps = {
  issue: IssueTimelineIssueFragment$key | null
  /** Callers holding the data outright (the Data Router page) pass it instead of a fragment ref. */
  timelineData?: IssueTimelineIssueFragment$data
  /** The front timeline section selected by the page query. */
  initialTimelineIssue?: TimelineIssue
  /** Set by callers with no Relay store to write into; see `TimelinePanel`. */
  disableRelayLiveUpdates?: boolean
  issueSecondary?: IssueTimelineSecondaryFragment
  highlightedEvent: string | undefined
  page?: number
  // Passed rather than selected: adding them to the fragment changes `IssueViewerViewQuery` and
  // invalidates every cached issue, and the caller already holds all three.
  owner: string
  repo: string
  number: number
} & EventProps
export const IssueTimeline = ({
  issue,
  timelineData,
  initialTimelineIssue,
  disableRelayLiveUpdates,
  issueSecondary,
  viewer,
  highlightedEvent,
  page,
  owner,
  repo,
  number,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
}: IssueTimelineProps) => {
  const fragmentData = useFragment<IssueTimelineIssueFragment$key>(mainTimelineFragment, issue)
  // One of the two is always supplied; the fragment read yields null only for the data-router path.
  const data = (timelineData ?? fragmentData) as IssueTimelineIssueFragment$data

  return (
    <ClassicTimeline
      data={data}
      initialTimelineIssue={initialTimelineIssue}
      disableRelayLiveUpdates={disableRelayLiveUpdates}
      owner={owner}
      repo={repo}
      number={number}
      highlightedEvent={highlightedEvent}
      page={page}
      isTransferInProgress={issueSecondary?.isTransferInProgress}
      viewer={viewer}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      optionConfig={optionConfig}
    />
  )
}

/**
 * Classic (non-tabbed) timeline. Separated into its own component so that the
 * `useTimelineItems` hook (which depends on the classic fragments) is only
 * mounted when those fragments are present in the response.
 */
type ClassicTimelineProps = {
  data: IssueTimelineIssueFragment$data
  initialTimelineIssue?: TimelineIssue
  disableRelayLiveUpdates?: boolean
  owner: string
  repo: string
  number: number
  highlightedEvent: string | undefined
  page?: number
  isTransferInProgress?: boolean
} & EventProps
const ClassicTimeline = ({
  data,
  initialTimelineIssue,
  disableRelayLiveUpdates,
  owner,
  repo,
  number,
  viewer,
  highlightedEvent,
  page,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  isTransferInProgress,
}: ClassicTimelineProps) => {
  const useTanstack = isFeatureEnabled('issue_viewer_paved_path')
  const TimelineSource = useTanstack ? TanstackTimeline : RelayTimeline

  return (
    <TimelineSource
      data={data}
      initialTimelineIssue={initialTimelineIssue}
      disableRelayLiveUpdates={disableRelayLiveUpdates}
      owner={owner}
      repo={repo}
      number={number}
      viewer={viewer}
      highlightedEvent={highlightedEvent}
      page={page}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      optionConfig={optionConfig}
      isTransferInProgress={isTransferInProgress}
    />
  )
}

const RelayTimeline = (props: ClassicTimelineProps) => {
  const result = useTimelineItems({
    timelineData: props.data,
    highlightedEvent: props.highlightedEvent,
    page: props.page,
  })

  return <TimelineItemsPanel {...props} {...result} secondaryTimelineMode="relay" />
}

const TanstackTimeline = (props: ClassicTimelineProps) => {
  const result = useTimelineItemsTanstack({
    owner: props.owner,
    repo: props.repo,
    number: props.number,
    issueId: props.data.id,
    highlightedEvent: props.highlightedEvent,
    page: props.page,
    initialIssue: props.initialTimelineIssue,
  })

  return <TimelineItemsPanel {...props} {...result} secondaryTimelineMode="endpoint" />
}

type TimelineItemsPanelProps = ClassicTimelineProps &
  UseTimelineItemsCoreResult & {secondaryTimelineMode: 'relay' | 'endpoint'}

const TimelineItemsPanel = ({
  data,
  viewer,
  highlightedEvent,
  page,
  onCommentChange,
  onCommentReply,
  onCommentEditCancel,
  optionConfig,
  isTransferInProgress,
  disableRelayLiveUpdates,
  timelineItems,
  totalItemCount,
  highlightedReady,
  secondaryTimelineMode,
}: TimelineItemsPanelProps) => {
  return (
    <TimelinePanel
      timelineItems={timelineItems}
      totalItemCount={totalItemCount}
      highlightedEvent={highlightedEvent}
      highlightedReady={highlightedReady}
      issueId={data.id}
      repositoryId={data.repository.id}
      repositoryNameWithOwner={data.repository.nameWithOwner}
      issueUrl={data.url}
      page={page}
      isTransferInProgress={isTransferInProgress}
      viewer={viewer}
      disableLiveUpdates={disableRelayLiveUpdates}
      disablePreheat={disableRelayLiveUpdates}
      onCommentChange={onCommentChange}
      onCommentReply={onCommentReply}
      onCommentEditCancel={onCommentEditCancel}
      optionConfig={optionConfig}
      secondaryTimelineMode={secondaryTimelineMode}
    />
  )
}
